// QRCodeList.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import config from '../config/config';
import {
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogContentText,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as SvgIcon } from '../assets/icons/Svg.svg';
import { ReactComponent as PngIcon } from '../assets/icons/Png.svg';
import { ReactComponent as JpgIcon } from '../assets/icons/Jpg.svg';
import { ReactComponent as PdfIcon } from '../assets/icons/Pdf.svg';
import { ReactComponent as EditIcon } from '../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete.svg';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  RootDiv,
  Header,
  LogoContainer,
  Logo,
  Title,
  SearchContainer,
  ContentContainer,
  PageHeader,
  PageTitle,
  CreateButton,
  CreateButtonText,
  TableContainerStyled,
  DialogTitleStyled,
  DialogContentStyled,
  DialogActionsStyled,
  IconButtonGroup,
  CloseButton,
  ResponsiveButton,
  InstructionContainer,
} from './styles';

// Set axios defaults
axios.defaults.withCredentials = true;

// Function to truncate text with ellipsis
function truncateText(text, maxLength) {
  if (text.length > maxLength) {
    return `${text.substring(0, maxLength)}...`;
  }
  return text;
}

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#161616',
      light: '#FF4E26',
      dark: '#B22200',
    },
    secondary: {
      main: '#757575',
    },
    error: {
      main: '#f44336',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#161616',
      secondary: '#161616',
    },
  },
});

const AddEditQRCodeDialog = ({
  qrCodeData,
  isOpen,
  onSave,
  onCancel,
  isEditing,
}) => {
  const [editingData, setEditingData] = useState(qrCodeData);
  const [redirectUrlError, setRedirectUrlError] = useState('');

  const validateRedirectUrl = (url) => {
    const urlPattern = /^(http:\/\/|https:\/\/)/;
    if (!urlPattern.test(url)) {
      setRedirectUrlError('Redirect URL must start with http:// or https://');
      return false;
    }
    setRedirectUrlError('');
    return true;
  };

  const handleSave = async () => {
    const { redirect_link, from_param } = editingData;
    if (!validateRedirectUrl(redirect_link)) {
      return;
    }

    try {
      const response = isEditing
        ? await axios.put(`${config.apiUrl}/qrcodes/${editingData.id}`, {
            redirectLink: redirect_link,
            fromParam: from_param,
          })
        : await axios.post(`${config.apiUrl}/generate_qr_code/`, {
            redirectLink: redirect_link,
            fromParam: from_param,
          });

      if (response.status === 200) {
        onSave(response.data);
      } else {
        console.error('Failed to save QR code');
      }
    } catch (error) {
      console.error(
        `Error while ${isEditing ? 'updating' : 'adding'} QR code:`,
        error
      );
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <DialogTitleStyled>
          {isEditing ? 'Edit QR Code' : 'Add QR Code'}
        </DialogTitleStyled>
        <CloseButton>
          <IconButton onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </CloseButton>
      </div>

      <DialogContentStyled>
        <TextField
          label="Redirect Link"
          variant="outlined"
          fullWidth
          value={editingData.redirect_link}
          onChange={(e) => {
            setEditingData({
              ...editingData,
              redirect_link: e.target.value,
            });
            validateRedirectUrl(e.target.value);
          }}
          error={!!redirectUrlError}
          helperText={redirectUrlError}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.25rem',
              backgroundColor: '#F4F4F4',
            },
            '& .MuiInputLabel-root': {
              color: '#525252',
              fontSize: '1rem',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              lineHeight: '1.375rem',
              letterSpacing: '0.009375rem',
            },
          }}
        />
        <TextField
          label="Comment"
          variant="outlined"
          fullWidth
          value={editingData.from_param}
          onChange={(e) =>
            setEditingData({
              ...editingData,
              from_param: e.target.value,
            })
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.25rem',
              backgroundColor: '#F4F4F4',
            },
            '& .MuiInputLabel-root': {
              color: '#525252',
              fontSize: '1rem',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              lineHeight: '1.375rem',
              letterSpacing: '0.009375rem',
            },
          }}
        />
      </DialogContentStyled>

      <DialogActionsStyled>
        <ResponsiveButton
          variant="contained"
          onClick={onCancel}
          sx={{
            backgroundColor: '#393939',
            color: 'white',
            '&:hover': {
              backgroundColor: '#2e2e2e',
            },
          }}
        >
          Cancel
        </ResponsiveButton>
        <ResponsiveButton
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: '#F03D00',
            color: 'white',
            '&:hover': {
              backgroundColor: '#d83600',
            },
          }}
        >
          Save
        </ResponsiveButton>
      </DialogActionsStyled>
    </Dialog>
  );
};

const DeleteConfirmationDialog = ({ isOpen, onConfirm, onCancel }) => (
  <Dialog
    open={isOpen}
    onClose={onCancel}
    fullWidth
    maxWidth="xs"
    PaperProps={{
      style: {
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
      },
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <DialogTitleStyled>Delete QR code</DialogTitleStyled>
      <CloseButton>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </CloseButton>
    </div>
    <DialogContentStyled>
      <DialogContentText
        sx={{
          fontSize: '1rem',
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 400,
          lineHeight: '1.375rem',
          color: '#525252',
          padding: '0rem',
        }}
      >
        Are you sure you want to delete this QR code?
      </DialogContentText>
    </DialogContentStyled>
    <DialogActionsStyled>
      <ResponsiveButton
        variant="contained"
        onClick={onCancel}
        sx={{
          backgroundColor: '#393939',
          color: 'white',
          '&:hover': {
            backgroundColor: '#2e2e2e',
          },
        }}
      >
        No
      </ResponsiveButton>
      <ResponsiveButton
        variant="contained"
        onClick={onConfirm}
        sx={{
          backgroundColor: '#F03D00',
          color: 'white',
          '&:hover': {
            backgroundColor: '#d83600',
          },
        }}
      >
        Yes, delete
      </ResponsiveButton>
    </DialogActionsStyled>
  </Dialog>
);

const QRCodeList = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [qrcodes, setQrcodes] = useState([]);
  const [editingQRCode, setEditingQRCode] = useState(null);
  const [addingQRCode, setAddingQRCode] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [deletingQRCode, setDeletingQRCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredQrcodes, setFilteredQrcodes] = useState([]);
  const [usageData, setUsageData] = useState({});
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Fetch QR codes from your FastAPI backend
    axios
      .get(`${config.apiUrl}/qrcodes`)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data;
          setQrcodes(data);
          setFilteredQrcodes(data);

          // After loading QR codes, request their usage data in parallel
          const usagePromises = data.map((qrcode) =>
            axios
              .get(`${config.apiUrl}/qrcode_usage/${qrcode.id}`)
              .then((usageResponse) => {
                if (usageResponse.status === 200) {
                  return {
                    id: qrcode.id,
                    usage_count: usageResponse.data.usage_count,
                  };
                }
              })
              .catch((error) => {
                console.error(
                  `Error fetching usage for QR code ${qrcode.id}:`,
                  error
                );
                return { id: qrcode.id, usage_count: null };
              })
          );

          // Wait for all usage data to be fetched
          Promise.all(usagePromises).then((usageResults) => {
            const usageDataObj = {};
            usageResults.forEach((result) => {
              if (result) {
                usageDataObj[result.id] = result.usage_count;
              }
            });
            setUsageData(usageDataObj);
          });
        } else {
          window.location.href = '/login';
        }
      })
      .catch((error) => {
        window.location.href = '/login';
        console.error(error);
      });
  }, []);

  useEffect(() => {
    // Filter the QR codes based on the search query
    filterQrcodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleDownloadQRCode = async (qrcodeId, format) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/download/${qrcodeId}?format=${format}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download the QR code');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_code_${qrcodeId}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QR code:', error);
    }
  };

  const handleCancelAddQRCode = () => {
    setAddingQRCode(null);
  };

  const handleSaveEdit = (updatedQRCode) => {
    setQrcodes((prevQrcodes) =>
      prevQrcodes.map((qrcode) =>
        qrcode.id === updatedQRCode.id ? updatedQRCode : qrcode
      )
    );
    setEditingQRCode(null);
    window.location.reload();
  };

  const handleDeleteConfirmation = (qrCodeData) => {
    setDeletingQRCode(qrCodeData);
    setIsDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deletingQRCode) {
      try {
        // Make an API request to delete the QR code
        const response = await axios.delete(
          `${config.apiUrl}/qrcodes/${deletingQRCode.id}`
        );

        if (response.status === 200) {
          setQrcodes((prevQrcodes) =>
            prevQrcodes.filter((qrcode) => qrcode.id !== deletingQRCode.id)
          );
          setIsDeleteConfirmationOpen(false);
          window.location.reload();
        } else {
          console.error('Failed to delete QR code');
        }
      } catch (error) {
        console.error('Error while deleting QR code:', error);
      }
    }
  };

  const filterQrcodes = () => {
    const normalizedQuery = searchQuery.toLowerCase();
    const filtered = qrcodes.filter((qrcode) => {
      const fromParam = qrcode.from_param.toLowerCase();
      const redirectLink = qrcode.redirect_link.toLowerCase();
      return (
        fromParam.includes(normalizedQuery) ||
        redirectLink.includes(normalizedQuery)
      );
    });
    setFilteredQrcodes(filtered);
  };

  useEffect(() => {
    if (showSearch && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearch]);

  return (
    <ThemeProvider theme={theme}>
      <RootDiv>
        {/* Header */}
        <Header>
          <LogoContainer>
            <Logo src="/artec-logo.png" alt="Logo" />
            {!(isMobile && showSearch) && (
              <Title>QR Code Generator</Title>
            )}
          </LogoContainer>

          <SearchContainer showSearch={showSearch}>
            {!showSearch ? (
              <IconButton onClick={() => setShowSearch(true)}>
                <SearchIcon />
              </IconButton>
            ) : (
              <TextField
                variant="outlined"
                placeholder="Search for..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                inputProps={{ style: { padding: '0.5rem' } }}
                inputRef={inputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setShowSearch(false);
                          setSearchQuery('');
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </SearchContainer>
        </Header>

        {/* Content */}
        <ContentContainer>
          <PageHeader>
            <PageTitle>All QR codes</PageTitle>

            <CreateButton
              variant="contained"
              color="primary"
              onClick={() =>
                setAddingQRCode({ redirect_link: '', from_param: '' })
              }
            >
              <CreateButtonText>Create new</CreateButtonText>
            </CreateButton>
          </PageHeader>
          <InstructionContainer>
            1. Click on “Create new” button, and paste URL
            with https:// and UTM into a “Redirect link” field.<br/>
            2. Write in “Comment” field information about QR code, ex.
            Ray II Leaflet Formnext 2024, Tokyo/Leo Gift box Welcome letter PM 20.12.2024 <br/>
            3. Press “Save” button.<br/>
            4. Download created QR code in the required format.
            SVG/PDF vector formats, you can change the size to any, with excellent quality.
          </InstructionContainer>
          <TableContainerStyled>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      color: theme.palette.text.secondary,
                      backgroundColor: '#E0E0E0',
                      fontSize: '0.875rem',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      lineHeight: '1.125rem',
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    style={{
                      color: theme.palette.text.secondary,
                      backgroundColor: '#E0E0E0',
                      fontSize: '0.875rem',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      lineHeight: '1.125rem',
                    }}
                  >
                    Redirect Link
                  </TableCell>
                  <TableCell
                    style={{
                      color: theme.palette.text.secondary,
                      backgroundColor: '#E0E0E0',
                      fontSize: '0.875rem',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      lineHeight: '1.125rem',
                    }}
                  >
                    Comment
                  </TableCell>
                  <TableCell
                    style={{
                      color: theme.palette.text.secondary,
                      backgroundColor: '#E0E0E0',
                      fontSize: '0.875rem',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      lineHeight: '1.125rem',
                    }}
                  >
                    QR Code Usage
                  </TableCell>
                  <TableCell
                    style={{
                      color: theme.palette.text.secondary,
                      backgroundColor: '#E0E0E0',
                      fontSize: '0.875rem',
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      lineHeight: '1.125rem',
                    }}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQrcodes.map((qrcode) => (
                  <TableRow key={qrcode.id}>
                    <TableCell style={{ color: theme.palette.text.primary }}>
                      {qrcode.id}
                    </TableCell>
                    <TableCell style={{ color: theme.palette.text.primary }}>
                      {truncateText(qrcode.redirect_link, 50)}
                    </TableCell>
                    <TableCell style={{ color: theme.palette.text.primary }}>
                      {truncateText(qrcode.from_param, 50)}
                    </TableCell>
                    <TableCell style={{ color: theme.palette.text.primary }}>
                      {typeof usageData[qrcode.id] !== 'undefined'
                        ? usageData[qrcode.id]
                        : 'Loading...'}
                    </TableCell>
                    <TableCell>
                      <IconButtonGroup>
                        <IconButton
                          onClick={() =>
                            handleDownloadQRCode(qrcode.id, 'svg')
                          }
                        >
                          <SvgIcon />
                        </IconButton>

                        <IconButton
                          onClick={() =>
                            handleDownloadQRCode(qrcode.id, 'png')
                          }
                        >
                          <PngIcon />
                        </IconButton>

                        <IconButton
                          onClick={() =>
                            handleDownloadQRCode(qrcode.id, 'jpeg')
                          }
                        >
                          <JpgIcon />
                        </IconButton>

                        <IconButton
                          onClick={() =>
                            handleDownloadQRCode(qrcode.id, 'pdf')
                          }
                        >
                          <PdfIcon />
                        </IconButton>

                        <IconButton onClick={() => setEditingQRCode(qrcode)}>
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          onClick={() => handleDeleteConfirmation(qrcode)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </IconButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainerStyled>
        </ContentContainer>

        {editingQRCode && (
          <AddEditQRCodeDialog
            qrCodeData={editingQRCode}
            isOpen={!!editingQRCode}
            onSave={handleSaveEdit}
            onCancel={() => setEditingQRCode(null)}
            isEditing
          />
        )}

        {addingQRCode && (
          <AddEditQRCodeDialog
            qrCodeData={addingQRCode}
            isOpen={!!addingQRCode}
            onSave={(newQRCode) => {
              setQrcodes((prevQrcodes) => [...prevQrcodes, newQRCode]);
              setAddingQRCode(null);
              window.location.reload();
            }}
            onCancel={handleCancelAddQRCode}
            isEditing={false}
          />
        )}

        <DeleteConfirmationDialog
          isOpen={isDeleteConfirmationOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsDeleteConfirmationOpen(false)}
        />
      </RootDiv>
    </ThemeProvider>
  );
};

export default QRCodeList;